import { DecimalPipe } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  HostBinding,
  inject,
  Injector,
  Input,
  runInInjectionContext,
  ViewChild,
  EventEmitter,
  Output,
} from '@angular/core';
import { MatButtonModule, MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import {
  retriveCurrentRouteData,
  SiaNewRouterService,
  SiaSizeDetectorService,
  SiaWindowSize,
} from '@sia/shared';
import { PdfViewerModule, PdfViewerComponent } from 'ng2-pdf-viewer';
import { ScrollModeType } from 'ngx-extended-pdf-viewer';
import { Subscription } from 'rxjs';

@Component({
  selector: 'sia-pdf-viewer',
  templateUrl: './sia-pdf-viewer.component.html',
  styleUrls: ['./sia-pdf-viewer.component.scss'],
  standalone: true,
  imports: [
    MatIcon,
    PdfViewerModule,
    MatButtonModule,
    MatIconButton,
    DecimalPipe,
  ],
})
export class SiaPdfViewerComponent {
  @ViewChild(PdfViewerComponent) pdfComponent!: PdfViewerComponent;
  private sizeDetectorService = inject(SiaSizeDetectorService);
  #routerService = inject(SiaNewRouterService);
  #injector = inject(Injector);
  #cdr = inject(ChangeDetectorRef);
  #subs: Subscription[] = [];

  private _file: any;
  pdfSrc?: Uint8Array | null;
  pageData: any = {};
  loading: boolean = false;
  error: string | null = null;

  @HostBinding('class.popup')
  isPopup = false;

  @Output() matchesCountChange = new EventEmitter<{
    current: number;
    total: number;
  }>();
  private eventBusSubscribed = false;

  get file(): any {
    return this._file;
  }
  zoomLevel: number = 1;
  @Input() initialZoom: number = 1;
  @Input() set file(value: any) {
    if (!value) {
      return;
    }
    this._file = value;
    this.pdfSrc = null;
    this.error = null;
    this.loading = true;

    // Verifica che sia un Blob o File valido
    if (!(value instanceof Blob)) {
      this.error = 'Il file fornito non è un Blob o File valido';
      this.loading = false;
      return;
    }

    // Verifica il tipo MIME (se disponibile)
    if (value.type && value.type !== 'application/pdf') {
      this.error = `Tipo file non supportato: ${value.type}. Atteso: application/pdf`;
      this.loading = false;
      return;
    }

    const fileReader = new FileReader();
    fileReader.onload = () => {
      this.pdfSrc = new Uint8Array(fileReader.result as ArrayBuffer);
      this.loading = false;
      this.#cdr.detectChanges();
    };
    fileReader.onerror = () => {
      this.error = 'Errore durante la lettura del file PDF';
      this.loading = false;
      this.#cdr.detectChanges();
    };
    fileReader.readAsArrayBuffer(this._file);
  }
  size: SiaWindowSize | null = null;
  @Input() scrollMode: ScrollModeType = 0;
  @Input() pageViewMode: 'single' | 'book' | 'multiple' | 'infinite-scroll' =
    'multiple';
  @Input() spread: 'off' | 'even' | 'odd' = 'odd';
  ready: boolean = false;

  /** Inserted by Angular inject() migration for backwards compatibility */

  constructor() {}

  ngOnInit(): void {
    let s0 = this.#routerService.routeChanged$.subscribe(() => {
      var pageData: any = null;
      runInInjectionContext(this.#injector, () => {
        pageData = retriveCurrentRouteData() ?? {};
      });
      if (!pageData?.guid || pageData?.guid == this.pageData?.guid) {
        return;
      }
      this.pageData = pageData;
      this.isPopup = this.pageData.outlet == 'popup';
    });
    this.#subs.push(s0);
  }
  ngAfterViewInit(): void {
    this.size = this.sizeDetectorService.currentSize;
    this.zoomLevel = this.initialZoom;
    this.ready = true;
  }

  zoomIn() {
    this.zoomLevel = Math.min(this.zoomLevel + 0.25, 3);
  }

  zoomOut() {
    this.zoomLevel = Math.max(this.zoomLevel - 0.25, 0.5);
  }

  resetZoom() {
    this.zoomLevel = 1;
  }

  onWheel(event: WheelEvent) {
    if (event.ctrlKey) {
      event.preventDefault();
      if (event.deltaY < 0) {
        this.zoomIn();
      } else {
        this.zoomOut();
      }
    }
  }

  search(
    query: string,
    type: 'find' | 'findagain' = 'find',
    previous: boolean = false,
  ) {
    if (this.pdfComponent && this.pdfComponent.eventBus) {
      if (!this.eventBusSubscribed) {
        this.eventBusSubscribed = true;
        this.pdfComponent.eventBus.on('updatefindmatchescount', (data: any) => {
          if (data && data.matchesCount) {
            this.matchesCountChange.emit(data.matchesCount);
          }
        });
        this.pdfComponent.eventBus.on('updatefindcontrolstate', (data: any) => {
          if (data && data.matchesCount) {
            this.matchesCountChange.emit(data.matchesCount);
          }
        });
      }
      if (query === '') {
        this.pdfComponent.eventBus.dispatch('find', {
          source: this.pdfComponent,
          type: '',
          query: '',
          caseSensitive: false,
          findPrevious: false,
          highlightAll: false,
          phraseSearch: true,
        });
        return;
      }
      this.pdfComponent.eventBus.dispatch('find', {
        source: this.pdfComponent,
        type: type === 'find' ? '' : 'again',
        query: query,
        caseSensitive: false,
        findPrevious: previous,
        highlightAll: true,
        phraseSearch: true,
      });
    }
  }
}
